import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/utils/theme";
import { useMemo } from "react";
import type { ClassNameValue } from "tailwind-merge";

interface Props {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  classNameItem?: ClassNameValue;
}

const TablePaginantion = ({
  hasNext,
  hasPrevious,
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  classNameItem,
  onNext,
  onPageChange,
  onPrevious,
}: Props) => {
  const paginationItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = new Set<number>();
    items.add(1);
    items.add(totalPages);

    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      if (i > 1 && i < totalPages) items.add(i);
    }

    const sorted = Array.from(items).sort((a, b) => a - b);
    const result: (number | string)[] = [];
    let prev = 0;

    for (const page of sorted) {
      if (prev > 0) {
        if (page - prev === 2) {
          result.push(prev + 1);
        } else if (page - prev > 2) {
          result.push("ELLIPSIS");
        }
      }
      result.push(page);
      prev = page;
    }
    return result;
  }, [currentPage, totalPages]);

  return (
    <div className="w-full flex items-center justify-between font-geist">
      <p className="text-sm text-body leading-2xl">
        Mostrando{" "}
        <span className="font-semibold text-body-loud">
          {currentPage === totalPages
            ? totalItems - (currentPage - 1) * pageSize
            : pageSize}
        </span>{" "}
        de <span className="font-semibold text-body-loud">{totalItems}</span>{" "}
        resultados.
      </p>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="cursor-pointer"
            />
          </PaginationItem>

          {paginationItems.map((item, index) => {
            if (item === "ELLIPSIS") {
              return (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationLink
                    className="size-8 cursor-pointer"
                    variant="quiet"
                    group="middle"
                  >
                    <PaginationEllipsis />
                  </PaginationLink>
                </PaginationItem>
              );
            }

            const page = item as number;
            return (
              <PaginationItem key={page} className="hidden tablet:block">
                <PaginationLink
                  className={cn(
                    "size-8 cursor-pointer",
                    classNameItem,
                    page === 1 && currentPage === page && "border-l",
                  )}
                  variant="quiet"
                  group="middle"
                  isActive={currentPage === page}
                  onClick={() => {
                    onPageChange(page);
                  }}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          <PaginationItem>
            <PaginationNext
              className="cursor-pointer"
              onClick={onNext}
              disabled={!hasNext}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default TablePaginantion;
